/**
 * View models for the Pro CRM client surfaces.
 *
 * The wire DTOs (lib/types.ts) are the source of truth for what the SERVER
 * sends. The components, however, render the richer design-doc shapes
 * (design §6.1): a per-client verification tier, a financials trio with
 * per-field stamps, an activity feed, attention signals. Some of those fields
 * are Phase-2 server slots not yet on the wire (verification tier, last-active,
 * newEvents); the fixtures supply them directly, and the prod adapter
 * (lib/adapt.ts) derives a Phase-1 approximation from the projected payload.
 *
 * Keeping the view model separate from the wire type means:
 *   - components depend only on the stable view model,
 *   - the dev fixture and the prod adapter both produce the SAME view model,
 *   - field-presence-is-the-contract is preserved: a status_only client's
 *     `financials` magnitudes are absent, so the UI shows "Not shared at this
 *     tier" exactly as it would against the real status_only projection.
 *
 * NOTE: there is intentionally NO readiness score anywhere in this surface.
 */

import type {
  ActivityEventType,
  ClientActivitySource,
  ConnectionMethod,
  ContactSource,
  EnrichmentState,
  FunnelState,
  NotificationChannel,
  NotificationFrequency,
  ProRole,
  SharingLevel,
  TodayGroupKey,
} from "./types";
import { PRO_FALLBACK_PRICE } from "@sill/shared/components/billing/pro-pricing-copy";

import type { TierRank } from "../components/tier/tier";
import type { Stage } from "./stage";

/** A single verified financial field with its verification stamp. */
export interface FinancialField {
  /** Formatted magnitude, e.g. "$214,000 / yr". Absent when not shared. */
  value?: string;
  /** Qualitative descriptor, e.g. "W-2 + RSU, 2 earners". */
  sub?: string;
  /** Verification verb, e.g. "Verified" | "Verified" | "Self-reported". */
  verb: string;
  /** Verification date display; null when self-reported / not stamped. */
  date: string | null;
  /** Per-field verification source. */
  kind: "bank" | "doc" | "self";
  /** Human-readable doc source ("2 pay stubs + 1099") — doc tier only. */
  docNote?: string;
}

export interface ClientFinancials {
  income: FinancialField;
  savings: FinancialField;
  debts: FinancialField;
}

export interface RateScenario {
  label: string;
  rate: string;
  payment: string;
  note: string;
}

/** LO-only underwriting block. Null until doc/bank verified (or status_only). */
export interface Underwriting {
  creditScore: number;
  creditPull: string;
  dti: string;
  frontDti: string;
  reserves: string;
  scenarios: RateScenario[];
}

export interface ClientActivityEvent {
  source?: ClientActivitySource;
  type: ActivityEventType;
  /** Plain text — never raw HTML (design §7.5.3). */
  text: string;
  /** Subject line for outbound sent-log rows. */
  subject?: string;
  /** Body text for outbound sent-log rows. */
  body?: string;
  /** Outbound channel for sent-log rows. */
  channel?: string;
  /** Bounded explanation for a non-sent outbound outcome. */
  reason?: string;
  /** Optional <b>-highlight fragments rendered by the component, not injected. */
  highlights?: string[];
  address?: string;
  price?: string;
  time: string;
}

export interface ClientNoteVM {
  id: string;
  body: string;
  author: string;
  createdAt: string;
  /** True when the note has been edited since creation (updated_at > created_at). */
  edited?: boolean;
}

/** Connection metadata flattened onto the view model for convenience. */
export interface ClientConnection {
  linkId: string;
  role: ProRole;
  sharingLevel: SharingLevel;
  method?: ConnectionMethod;
  funnelState: FunnelState;
  /** "since" display string, e.g. "Connected Apr 2026". */
  since?: string;
}

/**
 * The design-doc client shape (§6.1), enriched. `tier` is the dominant visual
 * element; `creditBand` is the agent-safe coarse band; `underwriting` is the
 * LO-only full block. Presentation-only signals (newEvents, minutesAgo,
 * needsResponse) drive the "Needs attention" sort + activity pills.
 */
export interface ClientVM {
  /**
   * Stable id used for routing — the consumer profile id in prod. A client
   * whose financials are hidden (locked / unavailable) carries no profile id,
   * so it is keyed by its contact id and `pickByContactId` is set.
   */
  id: string;
  /**
   * The People enrichment state this client was built from. Absent means
   * available (hand-authored fixtures, the legacy client read). When locked or
   * unavailable the verification tier is HIDDEN, not self-reported, so no
   * tier-based preset or filter may match it.
   */
  enrichmentState?: EnrichmentState;
  /** Set when `id` is the contact id: a campaign names this client by contact. */
  pickByContactId?: boolean;
  name: string;
  /**
   * Contact email + phone, read-through from the linked crm_contacts row
   * (SILL-427) — the pro's own annotation, not the consumer profile. Absent
   * when no contact is linked or the field is blank; never a fabricated value.
   */
  email?: string;
  phone?: string;
  /** Initials, e.g. "NP". */
  avatar: string;
  /** 1 = self, 2 = doc, 3 = bank (TierRank). */
  tier: TierRank;
  /** Full timeline string, e.g. "Buying in 0–3 months". */
  timeline: string;
  /** Short timeline chip, e.g. "0–3 mo". */
  timelineShort: string;
  /** Human-readable last activity, e.g. "18 minutes ago". */
  lastActivity: string;
  /** RFC3339 timestamp of the most recent outbound sent message. */
  lastSentAt?: string;
  /** Human-readable outbound sent message recency. */
  lastSentLabel?: string;
  /** Recency in minutes — sort key within a tier group. */
  minutesAgo: number;
  /** Recent change summary line. */
  changed: string;
  newEvents: number;
  activeThisWeek: boolean;
  changedThisWeek: boolean;
  /** Pro needs to reach out. */
  needsResponse: boolean;
  area: string;
  goal: string;
  /** Budget range display, e.g. "$680k–$720k" or a band when status_only. */
  budget: string;
  budgetLabel?: string;
  /** Coarse credit band for agents, e.g. "740–779" or "Not shared". */
  creditBand: string;
  financials: ClientFinancials;
  /** LO-only full block; null when status_only or unverified. */
  underwriting: Underwriting | null;
  activity: ClientActivityEvent[];
  /** Private notes written by the current pro; not consumer-visible activity. */
  notes?: ClientNoteVM[];
  connection: ClientConnection;
  /**
   * SILL-384 M6: the affordability headline (qual price + band + movement +
   * down-payment-ready). Optional so the many hand-authored test/fixture VMs
   * need not set it; the adapter always populates it from the projected DTO and
   * the card renders its `pending` state when absent.
   */
  affordability?: AffordabilityVM;
  /**
   * M3: the pro-owned crm_contacts row id for this linked client. Present only
   * when a contact record was found (the read-through is best-effort). Used as
   * the target for PATCH /api/professional/people/:contactId tag write-backs
   * (renamed from the retired /contacts/:contactId path, plan 377 M5, design
   * spec §4.1).
   */
  contactId?: string;
  /**
   * M3: tags from the linked crm_contacts row. Empty array when no contact
   * record exists or the contact has no tags. Optional so pre-M3 fixture VMs
   * are not forced to set it; consumers should default to `[]`.
   */
  contactTags?: string[];
  /**
   * M4: crm_contacts.notes read-through from the linked contact row. The
   * pinned "Contact note" card on client detail reads this and writes back
   * via PATCH /contacts/:id (D4 chokepoint). Absent / empty string when no
   * contact row is linked or the contact has no note.
   */
  contactNote?: string;
  /**
   * M8-B relationship-pipeline stage (plan 291, D8 rev 4), read-through from
   * the linked crm_contacts row. Absent when no contact is linked yet (a
   * defensive edge — the JIT pin guarantees every active link has one).
   * Drives the StageControl compact row under the header summary.
   */
  stage?: Stage;
  /** RFC3339 timestamp of the most recent stage write. Absent with `stage`. */
  stageUpdatedAt?: string;
  /**
   * D12 derived "quiet for 30+ days" overlay (plan 291 M8-B, task 1400).
   * Computed server-side at read time from stage + stageUpdatedAt, NEVER
   * stored. Absent/false means not dormant.
   */
  dormant?: boolean;
  /**
   * Plan 291 round-3 "Record agreement" affordance: the pro's OWN active
   * marketplace connection id for this client — present only on a
   * client-detail read (`adaptClientDetail`), absent on roster rows. Drives
   * the "Record agreement" button on `StageControl` when present AND
   * `stage === "connected"`.
   */
  connectionId?: string;
}

/** Recent band-high movement (partner-lens only) for the affordability headline. */
export interface AffordabilityMovementVM {
  direction: "up" | "down";
  /** Absolute band-high delta, e.g. "$25k". */
  amount: string;
  /** Coarse relative date, e.g. "this week". */
  asOf: string;
}

/**
 * The affordability headline (SILL-384 M6 / design §3.3). Projected from the
 * readiness/qualification OUTCOME — the FE never computes it. A null money field
 * is a deliberate PENDING state ("never $0, never a fabricated number").
 */
export interface AffordabilityVM {
  /** Sill qualification price headline, e.g. "$725,000". Null = pending. */
  qualPrice: string | null;
  /** Qualifying band, e.g. "$680k–$720k". Null when band data is absent. */
  band: string | null;
  /** Tight band for the roster column, e.g. "$680–720k". Null when absent. */
  bandCompact: string | null;
  /** Recent band movement (partner-lens only). Null when no movement/snapshot. */
  movement: AffordabilityMovementVM | null;
  /** Down-payment readiness; "pending" when the snapshot factor is nil. */
  downPayment: "ready" | "not_ready" | "pending";
  /** The buyer's own target budget, e.g. "$700k". Null when unshared. */
  targetBudget: string | null;
  targetBudgetLabel?: string;
  /** True when no qual price is computed yet → the card shows its pending headline. */
  pending: boolean;
  /**
   * The readiness OUTCOME the agent communicates over (2026-06-10 redesign).
   * `score` is the Sill readiness score (null for a not-yet-qualified / DNQ
   * client — render the segment, never a 0); `segment` is the enum and
   * `segmentLabel` the human label, e.g. "Getting Close" / "Not Yet Qualified".
   * Null when the projection carries no readiness outcome (pending).
   */
  score: number | null;
  segment: string | null;
  segmentLabel: string | null;
  /**
   * The buyer's TARGET price — a fixed number in our system, compact ("$550k").
   * The Sill score is relative to this target. Null when no target is set. This
   * is the exact goal price, NOT a band: we never render a separate target band.
   */
  target: string | null;
}

/** Cross-client buyer-signal feed item (design §6.2). */
export interface SignalEvent {
  clientId: string;
  who: string;
  /** Undefined = tier unknown (no payload hint / billing-locked profile). */
  tier?: TierRank;
  /** Plain text with optional highlight fragments (no raw HTML). */
  text: string;
  highlights?: string[];
  time: string;
  tone: "hot" | "good" | "default";
  /**
   * The originating activity event type (SILL-384 t14) — drives opener-template
   * selection when the drawer is launched from this signal card. Optional so
   * hand-authored preview fixtures need not set it (they fall to the neutral
   * opener).
   */
  type?: ActivityEventType;
}

/** "Sill suggests" gradient card content (design §4.6). */
export interface SillSuggestion {
  clientId: string;
  /** Plain text body; highlight fragments rendered as <b> by the card. */
  text: string;
  highlights?: string[];
}

/** KPI strip metric (design §4.4). */
export interface KpiMetric {
  label: string;
  value: string;
  delta?: string;
  /** Visual tone for the number. */
  tone?: "default" | "bank" | "warn";
  /** Render the delta in a flat (muted) tone. */
  flatDelta?: boolean;
}

/** The pro's own identity (sidebar + role gate). */
export interface ProUserVM {
  name: string;
  /** Account email (Clerk identity); absent in preview/when unavailable. */
  email?: string;
  org: string;
  role: ProRole;
  avatar: string;
}

/**
 * The resolved pro identity plus its load state. `useProUser` composes the
 * Clerk identity (name/email/avatar) with the professional profile (org/role),
 * so the org/role lag the name by one fetch — `isLoading` lets the sidebar show
 * a skeleton for the trailing fields instead of flashing an empty org.
 */
export interface ProUserResult {
  user: ProUserVM;
  isLoading: boolean;
}

// ─────────────────────────────────────────────────────────────────────────
// Contacts (book of business). The view model flattens the wire
// `{contact, funnel_state}` pair into one display shape. A contact may be a
// not-yet-linked lead OR a connected client — the funnel chip is the only
// signal, and (privacy) it NEVER implies Sill membership of an un-invited
// contact: "added" simply means "in your book, no invite sent".
// ─────────────────────────────────────────────────────────────────────────

export interface ContactVM {
  id: string;
  /** Display name; falls back to the email local-part, else "Unnamed contact". */
  name: string;
  /** Initials for the avatar (max 2). */
  avatar: string;
  email?: string;
  phone?: string;
  /** Raw first name from the contact row (for editing in the drawer). */
  firstName: string;
  /** Raw last name from the contact row (for editing in the drawer). */
  lastName: string;
  notes: string;
  tags: string[];
  source: ContactSource;
  funnelState: FunnelState;
  /** Present only when the contact became a connected client. */
  linkedProfileId?: string;
  /** Human-readable "Added Mar 2026" style string. */
  added: string;
  /** Freeform geographic area annotation. Empty string when not set. */
  area: string;
  /** Freeform purchase-timeline annotation. Empty string when not set. */
  horizon: string;
  /** RFC3339 timestamp when archived; undefined/null when active. */
  archivedAt?: string | null;
  /**
   * RFC3339 timestamp after which the contact is eligible for a new invite.
   * Null / undefined when no dispatched invite exists or the 72h window
   * cleared. The invite button renders disabled with "Invited Xd ago" while
   * the value is in the future. D2: anchored on dispatched_at.
   */
  inviteCooldownUntil?: string | null;
  /**
   * M8-B relationship-pipeline stage (plan 291, D8 rev 4). Always present on
   * a live-adapted contact (crm_contacts.stage defaults to "connected" on
   * every row); optional here so pre-M8-B hand-authored fixtures keep
   * compiling. Absent → the StageControl row is omitted (never a fabricated
   * default).
   */
  stage?: Stage;
  /** RFC3339 timestamp of the most recent stage write. Absent with `stage`. */
  stageUpdatedAt?: string;
  /**
   * D12 derived "quiet for 30+ days" overlay (plan 291 M8-B, task 1400).
   * See ClientVM.dormant.
   */
  dormant?: boolean;
}

/** Contact-first People row; contactId survives every connection transition. */
export interface PersonVM {
  contactId: string;
  name: string;
  avatar: string;
  /** Verification source shown beside the person identity (1 self / 2 document / 3 bank). Defaults to self when older callers omit it. */
  verificationTier?: 1 | 2 | 3;
  email?: string;
  phone?: string;
  /** Pro-owned area annotation; absent rather than invented when it is unset. */
  area?: string;
  tags: string[];
  relationshipStage: string;
  /** When the current relationship stage was set, for the roster stage age. */
  stageUpdatedAt?: string;
  sillStatus: "not_invited" | "invited" | "joined_sill" | "connected";
  timeline: string;
  timelineShort: string;
  engagement: {
    attention: number;
    actionableReason?: string;
    nextTaskDueAt?: string;
    latestNoteAt?: string;
    latestInviteAt?: string;
    latestActivityAt?: string;
    lastOutboundAt?: string;
    lastOutboundStatus?: string;
  };
  affordability?: {
    band: string;
    verificationLabel?: string;
    movement?: AffordabilityMovementVM | null;
  };
  enrichmentState: "not_connected" | "available" | "unavailable" | "locked";
  capabilities: {
    canInvite: boolean;
    canEmail: boolean;
    canSchedule: boolean;
    canTask: boolean;
    canNote: boolean;
    canInAppMessage: boolean;
    canDelete?: boolean;
    canRestore?: boolean;
  };
}

/** The funnel breakdown shown above the contacts table. */
export interface ContactFunnelSummaryVM {
  added: number;
  invited: number;
  opened: number;
  signed_up: number;
  linked: number;
}

/** A parsed CSV import preview, ready for the column-mapping confirm step. */
export interface ImportPreviewVM {
  batchId: string;
  filename: string;
  headers: string[];
  /** Heuristic suggested mapping: field → 0-based column index (or -1). */
  suggestedMapping: ImportFieldMapping;
  previewRows: string[][];
  totalRows: number;
}

/** Field → column-index map (−1 = unmapped). Mirrors ImportColumnMapping. */
export interface ImportFieldMapping {
  email: number;
  first_name: number;
  last_name: number;
  phone: number;
}

/** The commit outcome counts (design: {imported, skipped_duplicate, invalid}). */
export interface ImportResultVM {
  imported: number;
  skippedDuplicate: number;
  invalid: number;
  importedContactIds: string[];
}

/** The mass-invite send outcome (no per-email detail — privacy contract). */
export interface InviteResultVM {
  /** Dispatched OR silently suppressed — folded so a 1-contact send is no oracle. */
  sent: number;
  skippedAlreadySent: number;
  /** Skipped because they were invited within the last 72 hours. */
  skippedCooldown: number;
  invalid: number;
  /** Not sent because today's invite budget was spent; re-send after resetsAt. */
  skippedRateLimited?: number;
  resetsAt?: string;
  limit?: number;
  plan?: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Batch invite — preflight + async job polling.
// ─────────────────────────────────────────────────────────────────────────

/** Preflight six-bucket result (no-oracle: suppression reason not revealed). */
export interface PreflightResultVM {
  eligible: string[];
  alreadyInvited: string[];
  signedUp: string[];
  linked: string[];
  noEmail: string[];
  suppressed: string[];
  /** True when the server capped the candidate set; the frozen ids cover only
   * the first N matching contacts. The FE surfaces a warning in the batch bar. */
  truncated?: boolean;
}

/** Batch job failed-contact entry. */
export interface BatchFailedEntryVM {
  contactId: string;
  reason: string;
}

/** Batch job polling result. */
export interface BatchJobVM {
  jobId: string;
  /** "queued" | "running" | "done" */
  status: string;
  total: number;
  sent: number;
  failed: BatchFailedEntryVM[];
}

// ─────────────────────────────────────────────────────────────────────────
// Cross-client activity feed (the /activity surface). One row per projected
// event. `magnitude` is present ONLY when the payload carried it (full
// financial scope); a status_only event renders label + direction only.
// ─────────────────────────────────────────────────────────────────────────

export interface ActivityFeedItem {
  /** Stable key for the list. */
  id: string;
  type: ActivityEventType;
  /** The connected client this event belongs to (display name / area). */
  who: string;
  /**
   * Tier when the projected payload actually carried a hint (tier_label, or a
   * full-financial magnitude). Undefined = unknown — the row renders no tier
   * chip. The backend strips tier hints for billing-locked profiles
   * (SILL-521), so a proxy default here would be a false claim.
   */
  tier?: TierRank;
  /** Plain text headline — never raw HTML. */
  text: string;
  highlights?: string[];
  /** Magnitude string when full-financial scope carried one (else undefined). */
  magnitude?: string;
  /** ISO timestamp — used for day-grouping + relative display. */
  occurredAt: string;
  /** Coarse relative time, e.g. "18 min ago". */
  time: string;
  /** Urgency tone for the left accent. */
  tone: "hot" | "good" | "default";
  /** Route target for the row (the client detail). */
  profileId: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Today queue view models.
// ─────────────────────────────────────────────────────────────────────────

export interface TodayTaskVM {
  id: string;
  title: string;
  dueAt?: string;
  dueLabel: string;
  createdAt: string;
  doneAt?: string;
  contact?: {
    id: string;
    name: string;
    email?: string;
  };
}

export interface TodaySignalVM {
  kind: "signal";
  itemKey: string;
  group: TodayGroupKey;
  client: ClientVM;
  /**
   * Verification tier when KNOWN. Undefined = the backend withheld it
   * (enrichment locked on a Free plan, or the consumer shares less than full
   * financials) — render NO tier UI, never a false "Self-reported". Read this,
   * not client.tier: the embedded ClientVM's mandatory tier collapses unknown
   * to 1 (PR #638's bug class).
   */
  verificationTier?: TierRank;
  /** Enrichment discriminator for this item (mirrors Person.enrichment.state). */
  enrichmentState?: "not_connected" | "available" | "unavailable" | "locked";
  eventType: ActivityEventType;
  occurredAt: string;
  reason: string;
  reasonHighlights?: string[];
  lastSentAt?: string;
  lastSentLabel?: string;
  lastActivityAt?: string;
  goingCold: boolean;
}

export interface TodayTaskItemVM {
  kind: "task";
  itemKey: string;
  group: TodayGroupKey;
  task: TodayTaskVM;
  contactLabel?: string;
  dueLabel?: string;
}

export type TodayQueueItemVM = TodaySignalVM | TodayTaskItemVM;

export interface TodayGroupVM {
  key: TodayGroupKey;
  count: number;
  items: TodayQueueItemVM[];
}

/** View-model for one pending automation draft in the Today surface. */
export interface TodayDraftVM {
  /** automation_pending_drafts.id */
  id: string;
  /** Parent automation's UUID. */
  automationId: string;
  /** Buyer whose draft is pending review. */
  profileId: string;
  /** Template to review. */
  templateId: string;
  /** When the draft was enqueued (ISO 8601). */
  createdAt: string;
}

/** View-model for the pending marketplace-connection-requests summary row. */
export interface TodayMarketplaceRequestsVM {
  count: number;
  topFeeCents?: number;
}

export interface TodayQueueVM {
  groups: TodayGroupVM[];
  snoozed: TodaySignalVM[];
  /**
   * Pending automation drafts. NOT included in openCount — surfaced separately
   * and must not contribute to the badge count.
   */
  drafts: TodayDraftVM[];
  /**
   * Pending marketplace connection requests summary. NOT included in
   * openCount — same badge discipline as `drafts`. Optional: only present
   * once the client-side merge (see `stores/today.ts`) has resolved.
   */
  marketplaceRequests?: TodayMarketplaceRequestsVM;
  openCount: number;
  linkedCount: number;
  truncated: boolean;
}

// ─────────────────────────────────────────────────────────────────────────
// Pro profile + founding (the /profile surface).
// ─────────────────────────────────────────────────────────────────────────

export type ProProfileRole = "agent" | "lo";

export interface ProProfileVM {
  role: ProProfileRole;
  name: string;
  avatar: string;
  brokerage: string;
  /** NMLS for LOs, license number for agents — labeled by role in the view. */
  licenseLabel: string;
  licenseValue: string;
  serviceAreas: string[];
  bio: string;
  /** Public founding-directory opt-in. */
  directoryPublic: boolean;
  /** The shareable agent invite code (when present). */
  inviteCode?: string;
  /** Agent booking / scheduling page URL. Empty string when unset or LO. */
  bookingLink: string;
  founding: FoundingVM;
}

/** Founding-program membership + deterministic progress (design §4.11). */
export interface FoundingVM {
  member: boolean;
  programSlug?: string;
  invitesSent: number;
  clientsLinked: number;
  clientsVerified: number;
  /** The founding goal (linked clients to unlock). Display-only. */
  goal: number;
}

// ─────────────────────────────────────────────────────────────────────────
// Notification preferences (the /settings surface). The view model groups the
// flat {channel, category, frequency} rows into a per-category switch model.
// ─────────────────────────────────────────────────────────────────────────

export interface NotificationPrefVM {
  /** Stable category key, e.g. "weekly_digest". */
  category: string;
  /** Human label, e.g. "Weekly digest". */
  label: string;
  /** One-line description of what this controls. */
  description: string;
  channel: NotificationChannel;
  /** Current frequency; "off" renders the switch off. */
  frequency: NotificationFrequency;
  /** The frequency applied when the switch flips ON (e.g. "weekly"). */
  onFrequency: NotificationFrequency;
}

// ─────────────────────────────────────────────────────────────────────────
// Marketplace payments (M6-A) — the pro Settings Billing section.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Card-on-file status for the Billing section. No id field — mirrors the
 * server DTO's no-Stripe-id contract (BillingStatus in types.ts).
 */
export interface BillingStatusVM {
  hasCard: boolean;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  /** Derived from expMonth/expYear vs. today. */
  isExpired: boolean;
}

/**
 * Catalog-driven pricing for the CRM subscription — lets the FE render the
 * live price instead of hardcoded literals. `null` when the backend hasn't
 * shipped the `catalog` field yet (backward compat; callers fall back to the
 * prior literal $49/mo · $490/yr copy).
 */
export interface CRMBillingCatalogVM {
  monthlyCents: number;
  annualCents: number;
  foundingDiscountPercent: number;
  foundingDiscountMonths: number;
}

/**
 * FALLBACK_CATALOG is the single home for the pre-catalog literal pricing
 * copy (the historical $49/mo · $490/yr figures) — read ONLY when
 * `CRMBillingSummaryVM.catalog` is null (the backend hasn't shipped the
 * `catalog` field yet, or M5's effective-dated catalog resolution failed
 * closed and the backend fell back to its own const — see
 * crmbilling.CurrentCatalog()). Every other CRM billing surface renders the
 * live `catalog` off the summary; no other file should re-type these
 * numbers (pinned by crm-billing-hardcode.test.ts).
 */
export const FALLBACK_CATALOG: Pick<
  CRMBillingCatalogVM,
  "monthlyCents" | "annualCents"
> = {
  monthlyCents: PRO_FALLBACK_PRICE.monthlyCents,
  annualCents: PRO_FALLBACK_PRICE.annualCents,
};

/**
 * The account's resolved offer (backend `GET /api/professional/billing`
 * `offer` block) — genuinely dynamic to the campaign/program that redeemed
 * it, rather than the one global `CRMBillingCatalogVM` row every "50% off
 * your first 12 months" surface used to read from. `discountPercent` /
 * `discountMonths` come from the SAME `foundingDiscountForAccount` resolver
 * the backend mints its Stripe coupon from — the wire promise and the
 * eventual charge can never disagree. None of the three fields are ever
 * `undefined` on a present offer block: a `0` is meaningful ("no discount" /
 * "no free month") and distinct from the block being entirely ABSENT (see
 * `CRMBillingSummaryVM.offer`'s own doc for the absent-vs-zero contract).
 */
export interface CRMBillingOfferVM {
  discountPercent: number;
  discountMonths: number;
  /** Frozen free_months promise from the account's acquisition snapshot. */
  freeMonths: number;
}

/**
 * The RESOLVED money picture for this account (backend `activation` block,
 * `crmbilling.ResolveActivationPricing`) — what it is actually charged and
 * what its starter credit actually buys.
 *
 * Read this; never recompute it. `catalog`, `offer` and
 * `foundingDiscountMonthsRemaining` are the INPUTS the backend resolved this
 * from, kept on the wire because they are honest per-account facts, but a
 * surface that re-derives an answer from them is a second implementation of
 * the pricing rules. That is not hypothetical: this block exists because the
 * frontend used to do exactly that and drifted, dividing a starter credit by
 * the discounted rate forever so an offer whose credit outlived its discount
 * window rendered "first 3 months on us" while the admin console and the
 * invoices both said two.
 *
 * The division of labour is: the backend owns these NUMBERS, this app owns
 * the SENTENCE (see `starter-activation-copy.ts`).
 *
 * `null` only when the backend response predates this block (a stale client
 * mid-deploy, or a preview fixture that hasn't been updated). Callers must
 * NOT fall back to computing one — go through `resolveActivation`, which
 * degrades to list price and promises nothing.
 */
export interface CRMBillingActivationVM {
  /**
   * The ONLY field to gate a discount promise on. Already includes both
   * eligibility and the remaining-window check — a non-zero `discountPercent`
   * by itself is a frozen historical fact, not permission to promise (#581).
   */
  discountActive: boolean;
  /** Meaningful only while `discountActive`. */
  discountPercent: number;
  /**
   * Months REMAINING in the window, never the frozen total — a
   * partly-consumed window must not claim its original duration. Meaningful
   * only while `discountActive`.
   */
  discountMonths: number;
  /** What this account pays today. Never re-apply `discountPercent` to these. */
  effectiveMonthlyCents: number;
  effectiveAnnualCents: number;
  /**
   * The annual lane's first charge, already net of the starter credit and
   * floored at zero. The annual lane's honest promise is the amount due
   * today — "first month on us" is monthly-shaped and a year is not a month.
   */
  annualDueTodayCents: number;
  /**
   * WHOLE months `starterPromisedCents` covers, priced month by month
   * (discounted inside the window, list after it). Rounds DOWN;
   * `starterHasRemainder` reports leftover cents so copy can say "plus part
   * of the next" instead of rounding a partial month up into a free one.
   */
  starterCoversMonths: number;
  starterHasRemainder: boolean;
}

/** CRM subscription summary. This intentionally excludes payment methods,
 * invoices, and every Stripe identifier; Checkout and the Portal own those. */
export interface CRMBillingSummaryVM {
  plan: "free" | "sill_pro_monthly" | "sill_pro_annual";
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  maxClients: number;
  ai: boolean;
  aiDailyCap: number;
  catalog: CRMBillingCatalogVM | null;
  /**
   * The account's PER-ACCOUNT resolved offer — `null` only when the backend
   * response predates this field (older cached responses, preview fixtures
   * that haven't been updated yet). Every founding/discount/free-month
   * rendering surface must read `offer` first and fall back to `catalog`'s
   * global figures ONLY when `offer` itself is `null` — never when a present
   * offer's fields happen to be `0` (that `0` is an honest "no discount for
   * this account", not a missing value). See `lib/founding-discount.ts`'s
   * `resolveFoundingDiscount` for the one shared precedence rule every
   * surface (activation-incentive, /welcome, /pricing) composes through.
   */
  offer: CRMBillingOfferVM | null;
  /**
   * F6: the account's promised starter credit in cents, 0 when none. Drives
   * /welcome's tiered copy — never claim a starter credit the backend
   * hasn't confirmed (see `starterActivationCopy` in WelcomePage.tsx).
   */
  starterPromisedCents: number;
  /**
   * Whole calendar months left in the account's founding-discount window
   * (backend `Summary.FoundingDiscountMonthsRemaining`). NOT optional on
   * the wire — 0 is load-bearing: it means the window is exhausted, distinct
   * from "never started" (which the backend degrades to the full window).
   * Every "N% off for N months" surface (nudge, banner, /welcome,
   * /pricing) must gate on `> 0` before rendering a founding-discount claim
   * — a founding member with 0 remaining has lapsed and must never see the
   * discount promise repeated (spec §3.4, #581), even when `offer` carries a
   * non-zero `discountPercent` (that value is a frozen historical fact, not
   * a live eligibility flag).
   */
  foundingDiscountMonthsRemaining: number;
  /**
   * The resolved money picture — see `CRMBillingActivationVM`. Every pricing
   * and promise surface reads this instead of deriving one from `catalog` /
   * `offer` / `foundingDiscountMonthsRemaining` above.
   */
  activation: CRMBillingActivationVM | null;
}

/**
 * SILL-521 M2 cluster 4 — the copilot's daily message allowance (GET
 * /api/professional/assistant/allowance). Drives the free-tier remaining-
 * turns line and the post-exhaustion composer swap in ChatDockPanel; `pro`
 * tier renders nothing new (see the trigger-table three-surface rule).
 */
export interface AssistantAllowanceVM {
  limit: number;
  used: number;
  remaining: number;
  tier: "free" | "pro";
}

/** One row of the pro-scoped rate card table. */
export interface RateCardRowVM {
  cause: string;
  tier: string;
  listCents: number;
  discountCents: number;
  grantLabel?: string;
  netCents: number;
}
